/**
 * RiskDashboard — unified risk dashboard.
 *
 * Replaces three fragmented components:
 *   - FinancialRiskDashboard     (static FAIR summary)
 *   - DynamicFinancialRiskDashboard (asset-event correlation)
 *   - EnhancedRiskDashboard      (enhanced dynamic analysis)
 *
 * All data flows through a single service (financialRisk.ts) hitting
 * the cached /api/v2/financial-risk endpoints, so every tab shows
 * the same underlying numbers.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  FireIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from './UI/Card';
import LoadingSpinner from './UI/LoadingSpinner';
import financialRiskService, {
  FinancialRiskSummary,
  TopRisksResponse,
  FinancialRiskTrends,
} from '../services/financialRisk';
import { useAuth } from '../contexts/AuthContext';
import { CHART_COLORS } from '../utils/chartColors';

// ── Types ─────────────────────────────────────────────────────────────────────

type ViewMode = 'summary' | 'trends' | 'top-risks';

interface RiskDashboardProps {
  /** Pass a number that increments to force a refetch across all tabs. */
  refreshTrigger?: number;
  className?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatBRL = (v: number) => BRL.format(v);

const IMPACT_COLORS: Record<string, string> = {
  critical: CHART_COLORS.dangerDark,
  high: CHART_COLORS.warningDark,
  medium: CHART_COLORS.warning,
  low: CHART_COLORS.success,
  minimal: CHART_COLORS.primary,
};

const CATEGORY_ICONS: Record<string, string> = {
  operational: '⚙️',
  financial: '💰',
  reputational: '🏢',
  compliance: '📋',
  strategic: '🎯',
};

function impactColor(level: string) {
  return IMPACT_COLORS[level?.toLowerCase()] ?? CHART_COLORS.gray;
}

// ── Sub-views ─────────────────────────────────────────────────────────────────

function SummaryView({
  summary,
}: {
  summary: FinancialRiskSummary;
  companyId: number;
}) {
  return (
    <div className='flex flex-col gap-6'>
      {/* KPI row */}
      <div className='flex gap-4 flex-wrap'>
        <Card className='flex-1 min-w-[200px] border-2 border-primary-200 dark:border-primary-800'>
          <CardContent>
            <div className='flex items-center gap-2 mb-1'>
              <ShieldCheckIcon className='h-5 w-5 text-primary-600' />
              <p className='text-sm text-gray-500 dark:text-gray-400'>Risco Residual (em aberto)</p>
            </div>
            <p className='text-2xl font-bold text-gray-900 dark:text-white'>
              {formatBRL(summary.residual_expected_value)}
            </p>
            <p className='text-xs text-gray-400 dark:text-gray-500 mt-1'>
              {summary.residual_assessments} avaliações ainda não mitigadas
            </p>
          </CardContent>
        </Card>

        <Card className='flex-1 min-w-[200px]'>
          <CardContent>
            <div className='flex items-center gap-2 mb-1'>
              <CurrencyDollarIcon className='h-5 w-5 text-success-600' />
              <p className='text-sm text-gray-500 dark:text-gray-400'>Risco Mitigado</p>
            </div>
            <p className='text-2xl font-bold text-gray-900 dark:text-white'>
              {formatBRL(summary.mitigated_expected_value)}
            </p>
            <p className='text-xs text-gray-400 dark:text-gray-500 mt-1'>
              Eventos já resolvidos ou classificados como falso positivo
            </p>
          </CardContent>
        </Card>

        <Card className='flex-1 min-w-[200px]'>
          <CardContent>
            <div className='flex items-center gap-2 mb-1'>
              <ChartBarIcon className='h-5 w-5 text-warning-600' />
              <p className='text-sm text-gray-500 dark:text-gray-400'>Avaliações de Risco</p>
            </div>
            <p className='text-2xl font-bold text-gray-900 dark:text-white'>
              {summary.total_assessments}
            </p>
            <p className='text-xs text-gray-400 dark:text-gray-500 mt-1'>
              Valor total (histórico): {formatBRL(summary.total_expected_value)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* By impact (residual) */}
      {Object.keys(summary.by_impact_residual).length > 0 && (
        <Card>
          <CardContent>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white mb-3'>
              Risco Residual por Nível de Impacto
            </h3>
            <div className='flex flex-col gap-2'>
              {Object.entries(summary.by_impact_residual).map(([level, data]) => (
                <div key={level} className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <span
                      className='inline-block w-3 h-3 rounded-full'
                      style={{ backgroundColor: impactColor(level) }}
                    />
                    <span className='text-sm text-gray-700 dark:text-gray-300 capitalize'>{level}</span>
                  </div>
                  <div className='flex items-center gap-6'>
                    <span className='badge badge-secondary'>{data.count} eventos</span>
                    <span className='text-sm font-semibold text-gray-900 dark:text-white'>
                      {formatBRL(data.expected_value)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* By category (residual) */}
      {Object.keys(summary.by_category_residual).length > 0 && (
        <Card>
          <CardContent>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white mb-3'>
              Risco Residual por Categoria
            </h3>
            <div className='flex flex-col gap-2'>
              {Object.entries(summary.by_category_residual).map(([cat, data]) => (
                <div key={cat} className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <span>{CATEGORY_ICONS[cat?.toLowerCase()] ?? '⚠️'}</span>
                    <span className='text-sm text-gray-700 dark:text-gray-300 capitalize'>{cat}</span>
                  </div>
                  <div className='flex items-center gap-6'>
                    <span className='badge badge-secondary'>{data.count}</span>
                    <span className='text-sm font-semibold text-gray-900 dark:text-white'>
                      {formatBRL(data.expected_value)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAIR methodology note */}
      <div className='rounded-xl border border-primary-100 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 p-4'>
        <div className='flex items-start gap-2'>
          <InformationCircleIcon className='h-5 w-5 text-primary-600 mt-0.5 shrink-0' />
          <div>
            <p className='text-sm font-semibold text-primary-700 dark:text-primary-300'>Metodologia FAIR™</p>
            <p className='text-xs text-gray-500 dark:text-gray-400'>
              Cálculos baseados no framework{' '}
              <strong>FAIR (Factor Analysis of Information Risk)</strong> — The Open Group O-RA.
              Benchmarks: IBM Cost of a Data Breach 2024 · Verizon DBIR 2024 · ENISA Threat
              Landscape 2024 · ANPD (LGPD) 2024.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrendsView({ trends }: { trends: FinancialRiskTrends }) {
  if (!trends.trends || trends.trends.length === 0) {
    return (
      <p className='text-center text-gray-500 dark:text-gray-400 py-8'>
        Nenhum dado de tendência disponível para o período selecionado.
      </p>
    );
  }

  const maxValue = Math.max(...trends.trends.map((t) => t.total_expected_value));

  return (
    <div>
      <p className='text-sm text-gray-500 dark:text-gray-400 mb-4'>
        {trends.period.start_date} → {trends.period.end_date}
      </p>

      <div className='flex flex-col gap-2'>
        {trends.trends.map((entry) => {
          const pct = maxValue > 0 ? (entry.total_expected_value / maxValue) * 100 : 0;
          return (
            <div key={entry.date} className='flex items-center gap-3'>
              <span className='text-xs text-gray-500 dark:text-gray-400' style={{ minWidth: 90 }}>
                {entry.date}
              </span>
              <div className='flex-1 bg-gray-100 dark:bg-gray-800 rounded' style={{ height: 20 }}>
                <div
                  className='bg-primary-600 rounded h-full transition-all duration-300'
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className='text-xs text-gray-700 dark:text-gray-300 text-right' style={{ minWidth: 100 }}>
                {formatBRL(entry.total_expected_value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopRisksView({ topRisks }: { topRisks: TopRisksResponse }) {
  if (!topRisks.top_risks || topRisks.top_risks.length === 0) {
    return (
      <p className='text-center text-gray-500 dark:text-gray-400 py-8'>
        Nenhum risco crítico identificado.
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      {topRisks.top_risks.map((risk, i) => (
        <Card key={risk.id}>
          <CardContent className='flex items-start justify-between gap-3'>
            <div className='flex items-center gap-3'>
              <span
                className='w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white'
                style={{ backgroundColor: i === 0 ? CHART_COLORS.danger : i === 1 ? CHART_COLORS.warning : CHART_COLORS.gray }}
              >
                {i + 1}
              </span>
              <div>
                <p className='text-sm font-semibold text-gray-900 dark:text-white'>
                  Evento #{risk.event_id} — {risk.risk_category}
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Prazo: {risk.timeframe} · Prob.: {(risk.probability * 100).toFixed(1)}%
                </p>
              </div>
            </div>
            <div className='text-right shrink-0'>
              <p className='text-sm font-bold' style={{ color: impactColor(risk.impact_level) }}>
                {formatBRL(risk.expected_value)}
              </p>
              <span
                className='inline-block mt-1 rounded-full px-2 py-0.5 text-xs font-semibold'
                style={{ backgroundColor: `${impactColor(risk.impact_level)}20`, color: impactColor(risk.impact_level) }}
              >
                {risk.impact_level}
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const TABS: { value: ViewMode; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'summary', label: 'Resumo', Icon: CurrencyDollarIcon },
  { value: 'trends', label: 'Tendências', Icon: ArrowTrendingUpIcon },
  { value: 'top-risks', label: 'Top Riscos', Icon: FireIcon },
];

const RiskDashboard: React.FC<RiskDashboardProps> = ({ refreshTrigger, className }) => {
  const { user } = useAuth();
  const [view, setView] = useState<ViewMode>('summary');
  const [trendDays] = useState(30);

  const companyId: number = (user as any)?.company_id ?? 1;

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery<FinancialRiskSummary>({
    queryKey: ['risk-dashboard-summary', companyId, refreshTrigger],
    queryFn: () => financialRiskService.getCompanyRiskSummary(companyId),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 min — aligned with server cache TTL
    refetchInterval: 10 * 60 * 1000,
  });

  const {
    data: trends,
    isLoading: trendsLoading,
  } = useQuery<FinancialRiskTrends>({
    queryKey: ['risk-dashboard-trends', companyId, trendDays, refreshTrigger],
    queryFn: () => financialRiskService.getCompanyRiskTrends(companyId, trendDays),
    enabled: !!user && view === 'trends',
    staleTime: 15 * 60 * 1000,
  });

  const {
    data: topRisks,
    isLoading: topRisksLoading,
  } = useQuery<TopRisksResponse>({
    queryKey: ['risk-dashboard-top-risks', companyId, refreshTrigger],
    queryFn: () => financialRiskService.getCompanyTopRisks(companyId, 10),
    enabled: !!user && view === 'top-risks',
    staleTime: 5 * 60 * 1000,
  });

  const isLoading =
    (view === 'summary' && summaryLoading) ||
    (view === 'trends' && trendsLoading) ||
    (view === 'top-risks' && topRisksLoading);

  if (summaryError) {
    return (
      <Card>
        <CardContent>
          <div className='flex items-center gap-2 text-danger-600'>
            <ExclamationTriangleIcon className='h-5 w-5' />
            <p>Erro ao carregar dados de risco financeiro.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent>
        {/* Header */}
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2'>
            <ShieldCheckIcon className='h-5 w-5 text-primary-600' />
            <h2 className='text-lg font-bold text-gray-900 dark:text-white'>Risco Financeiro</h2>
            <span
              title='Calculado com metodologia FAIR™ — The Open Group O-RA'
              className='badge badge-primary'
            >
              FAIR™
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className='border-b border-gray-200 dark:border-gray-700 mb-4'>
          <nav className='-mb-px flex gap-6 overflow-x-auto'>
            {TABS.map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => setView(value)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  view === value
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <Icon className='h-4 w-4' />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className='flex justify-center py-8'>
            <LoadingSpinner size='lg' />
          </div>
        ) : (
          <>
            {view === 'summary' && summary && (
              <SummaryView summary={summary} companyId={companyId} />
            )}
            {view === 'trends' && trends && (
              <TrendsView trends={trends} />
            )}
            {view === 'top-risks' && topRisks && (
              <TopRisksView topRisks={topRisks} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskDashboard;
